// apps/ystnj/src/components/ai/aiModuleConfig.ts

import type { AdmissionsAIContext } from './types';
import { resolveAssistantRouteFamily } from '../../config/ai-assistant/route-family-registry';

export type AIAssistantModule =
  | 'admissions'
  | 'financial-aid'
  | 'faculty'
  | 'students-families'
  | 'tuition'
  | 'settings'
  | 'attendance'
  | 'parent-portal'
  | 'general';

export type AIAssistantModuleConfig = {
  id: AIAssistantModule;
  label: string;
  entityLabel: string;
  baseDataSources: string[];
  supportsContextFetch: boolean;
  supportsApplicationContext: boolean;
  getGreeting: (context: AdmissionsAIContext) => string;
  getInputPlaceholder: () => string;
};

type GreetingByPersona = {
  parent?: string;
  staff?: string;
  default: string;
};

const resolvePersonaBucket = (context: AdmissionsAIContext): 'parent' | 'staff' => (
  context.personaRole?.toLowerCase() === 'parent' ? 'parent' : 'staff'
);

const selectGreeting = (greetings: GreetingByPersona, context: AdmissionsAIContext): string => {
  const persona = resolvePersonaBucket(context);
  return greetings[persona] ?? greetings.default;
};

const MODULE_CONFIGS: Record<AIAssistantModule, Omit<AIAssistantModuleConfig, 'getGreeting' | 'getInputPlaceholder'>
  & { greetings: GreetingByPersona; inputPlaceholder: string }> = {
  admissions: {
    id: 'admissions',
    label: 'Admissions',
    entityLabel: 'applications',
    baseDataSources: ['Applications', 'Timeline', 'Calendar'],
    supportsContextFetch: true,
    supportsApplicationContext: true,
    greetings: {
      parent: 'Hi! I can help with your admissions application, deadlines, and required documents.',
      staff: 'Hi! I can help review admissions applications, surface missing items, and summarize applicant status.',
      default: 'Hi! I can help with admissions applications, missing items, and enrollment status.',
    },
    inputPlaceholder: 'Message Intellio...',
  },
  'financial-aid': {
    id: 'financial-aid',
    label: 'Financial Aid',
    entityLabel: 'applications',
    baseDataSources: ['Financial Aid Applications', 'Documents', 'Appeals'],
    supportsContextFetch: true,
    supportsApplicationContext: true,
    greetings: {
      parent: 'Hi! I can help with financial aid documents, deadlines, and scholarship questions.',
      staff: 'Hi! I can help review financial aid applications, documents, and recommendations.',
      default: 'Hi! I can help with financial aid applications, documents, and review status.',
    },
    inputPlaceholder: 'Message Intellio...',
  },
  'students-families': {
    id: 'students-families',
    label: 'Students & Families',
    entityLabel: 'records',
    baseDataSources: ['Students', 'Families', 'Records'],
    supportsContextFetch: false,
    supportsApplicationContext: false,
    greetings: {
      parent: 'Hi! I can help with student records, family updates, and school communications.',
      staff: 'Hi! I can help with student profiles, family records, and enrollment updates.',
      default: 'Hi! I can help with student and family records, enrollment, and communications.',
    },
    inputPlaceholder: 'Message Intellio...',
  },
  faculty: {
    id: 'faculty',
    label: 'Faculty',
    entityLabel: 'faculty records',
    baseDataSources: ['Faculty Directory', 'Profiles', 'Communications'],
    supportsContextFetch: false,
    supportsApplicationContext: false,
    greetings: {
      staff: 'Hi! I can help with faculty profiles, teaching assignments, notes, and communications.',
      default: 'Hi! I can help with faculty profiles, teaching assignments, notes, and communications.',
    },
    inputPlaceholder: 'Message Intellio...',
  },
  tuition: {
    id: 'tuition',
    label: 'Tuition',
    entityLabel: 'tuition accounts',
    baseDataSources: ['Tuition Accounts', 'Billing', 'Payments', 'Adjustments'],
    supportsContextFetch: false,
    supportsApplicationContext: false,
    greetings: {
      staff: 'Hi! I can help summarize tuition accounts, billing operations, payment risk, and administrator follow-up.',
      default: 'Hi! I can help summarize tuition accounts, billing operations, payment risk, and administrator follow-up.',
    },
    inputPlaceholder: 'Ask about tuition accounts, billing actions, payments, or risk...',
  },
  settings: {
    id: 'settings',
    label: 'Settings',
    entityLabel: 'settings',
    baseDataSources: ['Settings', 'Policies', 'Access'],
    supportsContextFetch: false,
    supportsApplicationContext: false,
    greetings: {
      staff: 'Hi! I can help with settings, staff access, and policy configuration.',
      default: 'Hi! I can help with settings, staff access, and policy configuration.',
    },
    inputPlaceholder: 'Message Intellio...',
  },
  'parent-portal': {
    id: 'parent-portal',
    label: 'Parent Portal',
    entityLabel: 'family records',
    baseDataSources: ['Applications', 'Portal Calendar', 'Financial Aid'],
    supportsContextFetch: true,
    supportsApplicationContext: true,
    greetings: {
      parent: 'Hi! I can help summarize applications, deadlines, and next steps for your family.',
      default: 'Hi! I can help summarize applications, deadlines, and next steps for your family.',
    },
    inputPlaceholder: 'Message Intellio...',
  },
  attendance: {
    id: 'attendance',
    label: 'Attendance',
    entityLabel: 'attendance records',
    baseDataSources: ['Attendance Overview', 'New Records', 'Teacher View', 'Student History', 'Control Plane'],
    supportsContextFetch: false,
    supportsApplicationContext: false,
    greetings: {
      staff: 'Hi! I can help summarize attendance trends, record-entry gaps, and follow-up needs.',
      default: 'Hi! I can help summarize attendance trends, record-entry gaps, and follow-up needs.',
    },
    inputPlaceholder: 'Ask about attendance trends, records, student history, or control-plane readiness...',
  },
  general: {
    id: 'general',
    label: 'Operations',
    entityLabel: 'records',
    baseDataSources: ['Records', 'Activity', 'Documents'],
    supportsContextFetch: false,
    supportsApplicationContext: false,
    greetings: {
      default: 'Hi! I can help summarize records, surface missing items, and answer workflow questions.',
    },
    inputPlaceholder: 'Message Intellio...',
  },
};

export const resolveAIAssistantModule = (pathname?: string): AIAssistantModule => {
  const path = (pathname ?? '').toLowerCase();
  if (
    path.startsWith('/school/financialaid')
    || path.startsWith('/school/financial-aid')
    || path.startsWith('/admin/financial-aid')
    || path.startsWith('/financial-aid')
  ) {
    return 'financial-aid';
  }
  if (
    path.startsWith('/school/students')
    || path.startsWith('/admin/students')
  ) {
    return 'students-families';
  }
  if (
    path.startsWith('/school/faculty')
    || path.startsWith('/admin/faculty')
    || path.startsWith('/bridged/admin/faculty')
  ) {
    return 'faculty';
  }
  if (
    path.startsWith('/school/run-reports')
    || path.startsWith('/school/tuition')
    || path.startsWith('/administrator/tuition')
    || path.startsWith('/admin/tuition')
  ) {
    return 'tuition';
  }
  if (
    path.startsWith('/school/settings')
  ) {
    return 'settings';
  }
  if (
    path.startsWith('/school/attendance')
    || path.startsWith('/super-administrator/control-plane/attendance')
  ) {
    return 'attendance';
  }
  if (path.startsWith('/parent/portal') || path.startsWith('/portal/family')) {
    return 'parent-portal';
  }
  if (
    path.startsWith('/school/admissions')
    || path.startsWith('/admin/admissions')
    || path.startsWith('/admissions')
    || path.includes('admissions')
  ) {
    return 'admissions';
  }
  return 'general';
};

export const getAIAssistantModuleConfig = (pathname?: string): AIAssistantModuleConfig => {
  const moduleId = resolveAIAssistantModule(pathname);
  const base = MODULE_CONFIGS[moduleId];
  const routeFamily = resolveAssistantRouteFamily(pathname);
  const label = moduleId === 'general' ? routeFamily.label : base.label;
  const entityLabel = moduleId === 'general' ? routeFamily.entityLabel : base.entityLabel;
  const baseDataSources = moduleId === 'general' ? routeFamily.baseDataSources : base.baseDataSources;
  const inputPlaceholder = moduleId === 'general' ? routeFamily.inputPlaceholder : base.inputPlaceholder;
  return {
    id: base.id,
    label,
    entityLabel,
    baseDataSources,
    supportsContextFetch: base.supportsContextFetch,
    supportsApplicationContext: base.supportsApplicationContext,
    getGreeting: (context) => (
      moduleId === 'general'
        ? routeFamily.greeting
        : selectGreeting(base.greetings, context)
    ),
    getInputPlaceholder: () => inputPlaceholder,
  };
};

export const buildDataSourcesLabel = (
  config: AIAssistantModuleConfig,
  context: AdmissionsAIContext
): string => {
  const sources = [...config.baseDataSources];
  if (context.faqReference && !sources.includes('FAQ')) {
    sources.push('FAQ');
  }
  return sources.join(', ');
};
